import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useErrorLogService } from 'ErrorLogging';
import { useSendPing } from 'Status';
import { showToast } from 'Toast';
import { selfRegisterApi } from './SelfRegisterApi';
import { type SelfRegisterParams } from './TuitionAndRegistration.types';
import { LOCALE_PREFIX } from './constants';

type UseSelfRegisterResult = {
    // True while we wait on the server. Disables the register button.
    processingRegistration: boolean;
    selfRegister: (params: SelfRegisterParams) => Promise<void>;
};

/*
    Registers a full scholarship learner, who owes nothing and so never reaches Stripe. RTK Query does
    not go through $http, so CurrentUserInterceptor never merges the response push messages: the ping
    is what resyncs currentUser and moves the learner off this screen.
*/
export function useSelfRegister(): UseSelfRegisterResult {
    const { t } = useTranslation('back_royal');
    const ErrorLogService = useErrorLogService();
    const { sendPing } = useSendPing();
    const [selfRegisterMutation] = selfRegisterApi.useSelfRegisterMutation();
    const [processingRegistration, setProcessingRegistration] = useState(false);

    const selfRegister = useCallback(
        async (params: SelfRegisterParams) => {
            setProcessingRegistration(true);

            try {
                await selfRegisterMutation(params).unwrap();
            } catch (error) {
                showToast({ message: t(`${LOCALE_PREFIX}.registration_failed`), type: 'danger' });
                ErrorLogService.notify('Failed to self register for a full scholarship offer', error);
                setProcessingRegistration(false);
                return;
            }

            // The learner is registered by this point. A failed ping only leaves the screen stale, so
            // it must not report a failed registration.
            try {
                await sendPing();
            } catch (error) {
                ErrorLogService.notify('Failed to resync the current user after a self registration', error);
            }

            setProcessingRegistration(false);
        },
        [selfRegisterMutation, sendPing, t, ErrorLogService],
    );

    return { processingRegistration, selfRegister };
}
